/**
 * Keyframes Editor — behavior-panel settings for the Keyframes container.
 *
 * The keyframe card strip (Base + per-keyframe thumbnails, select/add/rename/
 * delete) lives in the viewport overlay now — see mount-keyframe-context.tsx —
 * so the thumbnails sit over the scene they depict. What's left here is the
 * authored configuration that has no place on the viewport: transition defaults
 * and the initial keyframe.
 */

import React from 'react';
import type { Keyframes, KeyframesData } from 'trace-viewer/behaviors';

export interface KeyframesEditorProps {
    data: KeyframesData;
    behavior: Keyframes;
    onChange: (data: KeyframesData) => void;
}

const styles = {
    container: {
        display: 'flex',
        flexDirection: 'column' as const,
        gap: '8px',
    },
    hint: {
        fontSize: '10px',
        color: '#777',
        padding: '0 2px',
    },
    sectionHeader: {
        fontSize: '11px',
        color: '#888',
        textTransform: 'uppercase' as const,
        letterSpacing: '0.5px',
        marginBottom: '4px',
        marginTop: '8px',
    },
    fieldRow: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        marginBottom: '4px',
    },
    label: {
        fontSize: '11px',
        color: '#777',
        minWidth: '60px',
    },
    input: {
        backgroundColor: '#333',
        border: '1px solid #555',
        borderRadius: '3px',
        color: '#fff',
        padding: '2px 4px',
        fontSize: '10px',
    },
};

export const KeyframesEditor: React.FC<KeyframesEditorProps> = ({ data, onChange }) => {
    const keyframeNames = Object.keys(data.keyframes ?? {});
    const duration = data.transitionDuration ?? 0.5;
    const easing = data.transitionEasing ?? 'easeInOut';

    return (
        <div style={styles.container}>
            <div style={styles.hint}>
                Select and edit keyframes from the strip over the viewport.
            </div>

            {/* Transition defaults */}
            <div style={styles.sectionHeader}>Transition Defaults</div>
            <div style={styles.fieldRow}>
                <label style={styles.label}>Duration</label>
                <input
                    type="number"
                    value={duration}
                    onChange={(e) => {
                        const v = parseFloat(e.target.value);
                        onChange({ ...data, transitionDuration: Number.isFinite(v) ? Math.max(0, v) : 0.5 });
                    }}
                    step={0.1}
                    min={0}
                    style={{ ...styles.input, width: '50px' }}
                />
                <span style={{ fontSize: '10px', color: '#555' }}>s</span>
                <select
                    value={easing}
                    onChange={(e) => onChange({ ...data, transitionEasing: e.target.value as typeof easing })}
                    style={{ ...styles.input, padding: '2px 4px', cursor: 'pointer' }}
                >
                    <option value="linear">linear</option>
                    <option value="easeIn">easeIn</option>
                    <option value="easeOut">easeOut</option>
                    <option value="easeInOut">easeInOut</option>
                </select>
            </div>

            {/* Initial keyframe */}
            <div style={styles.fieldRow}>
                <label style={styles.label}>Initial</label>
                <select
                    value={data.initialKeyframe ?? ''}
                    onChange={(e) => onChange({ ...data, initialKeyframe: e.target.value || undefined })}
                    style={{ ...styles.input, flex: 1, padding: '3px 6px', cursor: 'pointer' }}
                >
                    <option value="">— none —</option>
                    {keyframeNames.map((name) => (
                        <option key={name} value={name}>{name}</option>
                    ))}
                </select>
            </div>
        </div>
    );
};
